import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Input, Select, InputNumber, TimePicker, Row, Col } from 'antd';
import { Amenity } from '@/types/resources/property';
import dayjs from 'dayjs';

interface AmenityFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  initialValues?: Partial<Amenity>;
  title: string;
  blocks: Array<{ id: string; name: string }>;
}

const AmenityFormModal: React.FC<AmenityFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  title,
  blocks,
}) => {
  const { t } = useTranslation(['properties', 'common']);
  const [form] = Form.useForm();

  const amenityTypes = [
    { value: 'gym', label: t('amenity.types.gym') },
    { value: 'swimming_pool', label: t('amenity.types.swimming_pool') },
    { value: 'garden', label: t('amenity.types.garden') },
    { value: 'playground', label: t('amenity.types.playground') },
    { value: 'community_hall', label: t('amenity.types.community_hall') },
  ];

  const statusOptions = [
    { value: 'active', label: t('amenity.status.active') },
    { value: 'maintenance', label: t('amenity.status.maintenance') },
    { value: 'inactive', label: t('amenity.status.inactive') },
  ];

  const daysOfWeek = [
    { value: 'Monday', label: t('common:days.monday') },
    { value: 'Tuesday', label: t('common:days.tuesday') },
    { value: 'Wednesday', label: t('common:days.wednesday') },
    { value: 'Thursday', label: t('common:days.thursday') },
    { value: 'Friday', label: t('common:days.friday') },
    { value: 'Saturday', label: t('common:days.saturday') },
    { value: 'Sunday', label: t('common:days.sunday') },
  ];

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const transformedValues = {
        ...values,
        operatingHours: {
          open: values.operatingHours.open.format('HH:mm'),
          close: values.operatingHours.close.format('HH:mm'),
          days: values.operatingHours.days,
        },
        block: values.block ? {
          id: values.block,
          name: blocks.find(b => b.id === values.block)?.name || '',
        } : undefined,
        guideline: values.guideline ? {
          id: values.guideline,
          name: values.guidelineName,
        } : undefined,
      };
      delete transformedValues.guidelineName;
      onSubmit(transformedValues);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const initialFormValues = initialValues ? {
    ...initialValues,
    operatingHours: initialValues.operatingHours ? {
      open: dayjs(initialValues.operatingHours.open, 'HH:mm'),
      close: dayjs(initialValues.operatingHours.close, 'HH:mm'),
      days: initialValues.operatingHours.days,
    } : undefined,
    block: initialValues.block?.id,
    guideline: initialValues.guideline?.id,
    guidelineName: initialValues.guideline?.name,
  } : undefined;

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnHidden
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialFormValues}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('amenity.details.attributes.name')}
              rules={[{ required: true, message: t('amenity.validation.required') }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('amenity.details.attributes.type')}
              rules={[{ required: true, message: t('amenity.validation.required') }]}
            >
              <Select options={amenityTypes} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('amenity.details.attributes.description')}
          rules={[{ required: true, message: t('amenity.validation.required') }]}
        >
          <Input.TextArea rows={4} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="capacity"
              label={t('amenity.details.attributes.capacity')}
              rules={[{ required: true, message: t('amenity.validation.required') }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('amenity.details.attributes.status')}
              rules={[{ required: true, message: t('amenity.validation.required') }]}
            >
              <Select options={statusOptions} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="block"
              label={t('amenity.details.attributes.block')}
            >
              <Select
                allowClear
                options={blocks.map(block => ({ value: block.id, label: block.name }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="guideline"
              label={t('amenity.details.attributes.guideline')}
            >
              <Select
                allowClear
                options={[
                  { value: '1', label: 'Pool Guidelines' },
                  { value: '2', label: 'Gym Guidelines' },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('amenity.details.attributes.operatingHours')}
          required
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name={['operatingHours', 'open']}
                rules={[{ required: true, message: t('amenity.validation.required') }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={['operatingHours', 'close']}
                rules={[{ required: true, message: t('amenity.validation.required') }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name={['operatingHours', 'days']}
                rules={[{ required: true, message: t('amenity.validation.required') }]}
              >
                <Select
                  mode="multiple"
                  options={daysOfWeek}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AmenityFormModal; 